import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';

import { ChildGlossaryTermFragment } from '../../graphql/glossaryNode.generated';
import { useExportEntityMutation } from '../../graphql/mutations.generated';
import { GlossaryNodeFragment } from '../../graphql/fragments.generated';
import { GlossaryNode, GlossaryTerm } from '../../types.generated';

import { BulkAction } from '../shared/bulkAction/BulkAction';
import { useEntityRegistry } from '../useEntityRegistry';
import GlossaryEntityItem from './GlossaryEntityItem';
import { Notification } from '../shared/Notification';
import { PaginationBar } from '../entity/shared/tabs/Glossary/PaginationBar';
import { ComponentSpinner } from '../shared/ComponentSpinner';

const EntitiesWrapper = styled.div`
    flex: 1;
    padding: 4px;
    overflow: auto;
    background-color: #ffffff;

    a {
        display: block;
    }
`;

interface Props {
    page: number;
    pageSize: number;
    selectedItems: string[];
    onChangePage: (newPage: number) => void;
    setSelectedItems: (items: string[]) => void;
    nodes: (GlossaryNode | GlossaryNodeFragment)[];
    onShowSizeChange: (newPageSize: number) => void;
    terms: (GlossaryTerm | ChildGlossaryTermFragment)[];
    onBulkDeleteEntities?: () => void;
}

function GlossaryEntitiesList(props: Props) {
    const {
        nodes,
        page,
        pageSize,
        terms,
        selectedItems,
        onChangePage,
        setSelectedItems,
        onShowSizeChange,
        onBulkDeleteEntities,
    } = props;
    const entityRegistry = useEntityRegistry();

    const [isCsvExporting, setIsCsvExporting] = useState<boolean>(false);
    const [exportEntityMutation] = useExportEntityMutation();
    const [exportEntity, setExportEntity]: any = useState(null);

    const [paginatedItems, setPaginatedItems] = useState<
        Array<GlossaryNode | GlossaryNodeFragment | GlossaryTerm | ChildGlossaryTermFragment>
    >([]);
    const totalCount = nodes.length + terms.length;

    // Update paginated items when page, pageSize, nodes, or terms change
    useEffect(() => {
        const startIndex = (page - 1) * pageSize;
        // Combine nodes and terms into a single array
        const allItems = [...nodes, ...terms];
        const endIndex = Math.min(startIndex + pageSize, allItems.length);

        // Get paginated items for current page
        const currentPageItems = allItems.slice(startIndex, endIndex);
        setPaginatedItems(currentPageItems);
    }, [page, pageSize, nodes, terms]);

    const handleItemCheck = (event, glossary) => {
        event.preventDefault();
        event.stopPropagation();

        let updatedGlossaryUrns = [...selectedItems];

        if (updatedGlossaryUrns.includes(glossary.urn)) {
            updatedGlossaryUrns = updatedGlossaryUrns.filter((name) => name !== glossary.urn);
        } else {
            updatedGlossaryUrns.push(glossary.urn);
        }

        setSelectedItems(updatedGlossaryUrns);
    };

    /**
     * Handles the export of the selected datasets to a CSV file.
     * Calls the exportEntityMutation to export the selected datasets to a CSV file.
     * Displays a notification message to the user once the export is completed.
     * Clears the selectedItems state and sets the isAllSelected state to false.
     */
    const exportCsv = () => {
        const glossaryNodeUrns = nodes.filter((item) => selectedItems.includes(item?.urn)).map((item) => item?.urn);
        const glossaryTermUrns = terms.filter((item) => selectedItems.includes(item?.urn)).map((item) => item?.urn);

        setIsCsvExporting(true);
        setExportEntity(null);

        const input = {
            glossaryTerm: {
                glossaryNodeUrns: glossaryNodeUrns || [],
                glossaryTermUrns: glossaryTermUrns || [],
            },
            entityType: 'glossaryTerm',
        };

        exportEntityMutation({
            variables: {
                input,
            },
        })
            .then((res) => {
                if (res?.data) {
                    setExportEntity(res.data.exportEntity);
                    setSelectedItems([]);
                }
            })
            .catch((e) => {
                if (e.networkError.statusCode === 503) {
                    const updatedInput = {
                        ...input,
                        async: true,
                    };
                    // Retry with a different input for 504 errors
                    exportEntityMutation({
                        variables: {
                            input: updatedInput,
                        },
                    }).then((retryRes) => {
                        const { data } = retryRes;
                        setExportEntity(data?.exportEntity);
                        setSelectedItems([]);
                    });
                }
            })
            .finally(() => setIsCsvExporting(false));
    };

    return (
        <>
            {exportEntity && <Notification exportEntity={exportEntity} />}

            {isCsvExporting && <ComponentSpinner />}

            <EntitiesWrapper>
                {paginatedItems.map((item) => {
                    const isNode = nodes.some((node) => node.urn === item.urn);

                    if (isNode) {
                        const node = item as GlossaryNode | GlossaryNodeFragment;
                        return (
                            <GlossaryEntityItem
                                key={node.urn}
                                name={node.properties?.name || ''}
                                urn={node.urn}
                                type={node.type}
                                selectedItems={selectedItems}
                                handleItemCheck={(e) => handleItemCheck(e, node)}
                                count={(node as GlossaryNodeFragment).children?.total}
                            />
                        );
                    }
                    const term = item as GlossaryTerm | ChildGlossaryTermFragment;
                    return (
                        <GlossaryEntityItem
                            key={term.urn}
                            name={entityRegistry.getDisplayName(term.type, term)}
                            handleItemCheck={(e) => handleItemCheck(e, term)}
                            selectedItems={selectedItems}
                            urn={term.urn}
                            type={term.type}
                        />
                    );
                })}

                <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    loading={isCsvExporting}
                    onChangePage={onChangePage}
                    onShowSizeChange={onShowSizeChange}
                />
            </EntitiesWrapper>

            <BulkAction
                type="glossary"
                selectedItems={selectedItems}
                exportCsv={exportCsv}
                setSelectedItems={setSelectedItems}
                confirmGlossaryTermsDeletion={onBulkDeleteEntities}
            />
        </>
    );
}

export default GlossaryEntitiesList;
